import { Box, BoxProps, DialogContent, Stack, styled, Typography } from '@mui/material';
import { PortableText, PortableTextReactComponents } from '@portabletext/react';
import { useMemo } from 'react';
import type { ArbitraryTypedObject, PortableTextBlock, TypedObject } from '@portabletext/types';
import Tooltip from '@mui/material/Tooltip';
import { useDisclosure } from '@shared/hooks';
import { Dialog } from '../Dialog';

interface Props<B extends TypedObject = PortableTextBlock | ArbitraryTypedObject> extends BoxProps {
  body: B[];
}

const PreviewImage = styled('img')({
  maxWidth: '100%',
  height: 'auto',
  display: 'block',
  cursor: 'zoom-in',
});

const ExpandedImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '100vh',
  cursor: 'zoom-out',
});

function ZoomableImage({ url, alt }: { url: string; alt: string }) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <Box component="figure" sx={{ my: 2, marginInline: 0 }}>
        <PreviewImage src={url} alt={alt} onClick={onOpen} />
      </Box>
      <Dialog open={isOpen} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogContent onClick={onClose}>
          <ExpandedImage src={url} alt={alt} />
        </DialogContent>
      </Dialog>
    </>
  );
}

const portableComponents: Partial<PortableTextReactComponents> = {
  types: {
    image: ({ value }) => {
      const url = value?.asset?.url;
      if (!url) return null;
      return <ZoomableImage url={url} alt={value.caption ?? ''} />;
    },
  },
  block: {
    normal: ({ children }) => (
      <Typography sx={{ mt: 2, '&>br:first-child': { display: 'none' } }}>{children}</Typography>
    ),
    h1: ({ children }) => (
      <Typography variant="h4" component="h1" marginTop={4}>
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography variant="h4" component="h2" marginTop={3}>
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography variant="h5" component="h3" marginTop={3}>
        {children}
      </Typography>
    ),
    h4: ({ children }) => (
      <Typography variant="h6" component="h4" marginTop={3}>
        {children}
      </Typography>
    ),
    h5: ({ children }) => (
      <Typography variant="h6" component="h5" marginTop={2}>
        {children}
      </Typography>
    ),
    h6: ({ children }) => (
      <Typography variant="h6" component="h6">
        {children}
      </Typography>
    ),
    blockquote: ({ children }) => (
      <Typography
        variant="h4"
        component="span"
        sx={(theme) => ({
          marginBlock: 3,
          marginInline: 2,
          fontStyle: 'italic',
          display: 'flex',
          fontWeight: '400',
          lineHeight: 30.72 / 24,
          '&::before': {
            content: '""',
            width: theme.spacing(1.5),
            display: 'inline-block',
            marginRight: 3,
            borderRadius: 0.5,
            background: theme.palette.gradient.secondary,
          },
        })}
      >
        {children}
      </Typography>
    ),
  },
  marks: {
    concept: ({ children, value }) => (
      <Tooltip
        title={
          <Stack spacing={2}>
            <Typography variant="h4" component="h3">
              {value.reference.title}
            </Typography>
            <Typography>{value.reference.definition}</Typography>
          </Stack>
        }
      >
        <Box
          component="span"
          borderBottom={({ palette }) => `3px dotted ${palette.text.secondary}`}
        >
          {children}
        </Box>
      </Tooltip>
    ),
  },
};

function SanityBody({ body, component = 'section', sx, ...boxProps }: Props): JSX.Element {
  const bodyWithoutEmptyParagraphs = useMemo(
    () =>
      body &&
      body
        .filter(
          ({ children, _type }) =>
            _type !== 'block' ||
            !!children?.some(
              ({ text, _type }: { text: any; _type: any }) => _type !== 'span' || !!text
            )
        )
        // PortableText rejects blocks with `markDefs: null` and silently renders nothing
        .map((block) =>
          block._type === 'block' && block.markDefs == null ? { ...block, markDefs: [] } : block
        ),
    [body]
  );

  return (
    <Box
      component={component}
      sx={{
        '& > :first-child': {
          mt: 0,
        },
        ...sx,
      }}
      {...boxProps}
    >
      <PortableText value={bodyWithoutEmptyParagraphs} components={portableComponents} />
    </Box>
  );
}

export default SanityBody;
